import React from 'react';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import {
  ModelGroupDTO,
  useModelPortfolioGroupsQuery,
} from '../../../../../../api/useModelPortfolioGroupsQuery';
import { ErrorState } from '../../../../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { ClientAccessSettingsState } from '../../../../../../ClientAccess.types';
import { OptionType } from '../../../../../../../CommonComponents/OPL/Select/BaseSelect/types';

interface Props {
  taxableAccountModelGroup?: ModelGroupDTO | null;
  nonTaxableAccountModelGroup?: ModelGroupDTO | null;
  agreementOptions?: OptionType<string>[];
  selectedAgreements?: string[] | null;
  isLoadingAgreementOptions?: boolean;
  onChange(data: Partial<ClientAccessSettingsState>): void;
}

export const AutoAccounts: React.FC<Props> = ({
  taxableAccountModelGroup,
  nonTaxableAccountModelGroup,
  onChange,
  agreementOptions,
  selectedAgreements,
  isLoadingAgreementOptions,
}) => {
  const {
    isLoading,
    isFetching,
    isError,
    refetch,
    data: modelPortfoliosGroups,
  } = useModelPortfolioGroupsQuery();

  const modelPortfoliosGroupsOptions = modelPortfoliosGroups
    ? modelPortfoliosGroups.map(({ groupId, groupName }) => ({
        value: groupId,
        label: groupName,
      }))
    : [];

  /**
   * SelectedAgreements doesn't come with label, so we must map it to existing
   * options. It is possible for agreementOptions to not contain a
   * selectedAgreement. For example, an agreementOption was made inactive. In
   * such case, we will not display selectedAgreement to client in the
   * onboarding workflow.
   */
  const selectedAgreementOptions = (selectedAgreements && agreementOptions
    ? selectedAgreements
        .map(id => ({
          value: id,
          label: agreementOptions.find(({ value }) => value === id)?.label,
        }))
        .filter(({ label }) => label)
    : []) as OptionType<string>[];

  if (isError && !isFetching) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" my="32">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  return (
    <section>
      <Box display="flex" gridGap={24} my="16">
        <Box flex={1}>
          <Text as="p" fontWeight={500} mb="4">
            Model Group Selection
          </Text>
          <Text as="p" fontSize="small" color="neutral.500">
            Select the model group that the user will be able to select from when completing this
            workflow for both taxable and non-taxable accounts.
          </Text>
        </Box>

        <Box width={240}>
          <Select
            isLoading={isLoading}
            value={modelPortfoliosGroupsOptions.find(
              ({ value }) => value === taxableAccountModelGroup?.groupId,
            )}
            label="Taxable Account Model Group"
            placeholder="Taxable"
            options={modelPortfoliosGroupsOptions}
            onChange={opt => {
              if (opt) {
                onChange({
                  taxableAccountModelGroup: modelPortfoliosGroups?.find(
                    ({ groupId }) => groupId === opt.value,
                  ),
                });
              }
            }}
            mb="16"
          />

          <Select
            isLoading={isLoading}
            value={modelPortfoliosGroupsOptions.find(
              ({ value }) => value === nonTaxableAccountModelGroup?.groupId,
            )}
            label="Non-Taxable Account Model Group"
            placeholder="Non-Taxable"
            options={modelPortfoliosGroupsOptions}
            onChange={opt => {
              if (opt) {
                onChange({
                  nonTaxableAccountModelGroup: modelPortfoliosGroups?.find(
                    ({ groupId }) => groupId === opt.value,
                  ),
                });
              }
            }}
          />
        </Box>
      </Box>

      <Box display="flex" gridGap={24} my="16">
        <Box flex={1}>
          <Text
            as="label"
            fontWeight={500}
            mb="4"
            htmlFor="select--agreements-selection"
            display="inline-block"
          >
            Agreements Selection
          </Text>
          <Text as="p" fontSize="small" color="neutral.500">
            Select the agreements and/or disclosures the client will need to agree to before opening
            an account.
          </Text>
        </Box>

        <Select
          id="agreements-selection"
          isMulti
          isLoading={isLoadingAgreementOptions}
          placeholder="Select agreements..."
          value={selectedAgreementOptions}
          options={agreementOptions}
          onChange={opts => {
            if (opts) {
              onChange({
                selectedAgreements: opts.map(({ value }) => value),
              });
            }
          }}
          css="width: 240px"
        />
      </Box>
    </section>
  );
};
